import React from "react";
import { useContext, useEffect } from "react";
import { useTheme } from "emotion-theming";
import { useHistory } from "react-router-dom";

import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";

import { getNudgeConfig } from "./helpers";
import { getPlatform } from "../../../helpers";
import { getStringFromId } from "helpers/vernacular";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";

import { trackCodNudgeVisible, trackCODNudgeCtaClick } from "../common/dependencies/tracking";

import { PAYMENT_MODES } from "constants/paymentModes";
import { DEVICE_TYPE, BUTTON_TYPE } from "constants/index";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";

import CashOnDelivery from "@nykaa/payment-sdk/src/CashOnDelivery/mobile";
import CashOnDeliveryDesktop from "@nykaa/payment-sdk/src/CashOnDelivery/desktop";
import { CommonContextContext } from "wrapper/CommonWrapper/context";


const CashOnDeliveryWrapper = ({
  doPayment,
  isAccordianOpened,
  toggleNewPaymentAccordian,
  endIcon,
  paymentFailureFlow = false,
  isDisabled,
  newPaymentMethodWithPriority,
  newPaymentMethod,
  isButtonFixedToBottom = false,
  disableNudge = false
}: any) => {
  const paymentContext: any = useContext(PaymentContext);
  const { applicationData } = useContext(ApplicationContext);
  const { orderAmount, paymentInfo, cartInfo} = useContext(CommonContextContext);
  
  const theme = useTheme() as Theme;
  const history = useHistory();
  const platform = getPlatform();

  // const paymentInfo = paymentContext?.paymentInfo;
  const grandTotal = orderAmount;
  // const cartInfo = paymentContext?.cartInfo;
  const codHandlingCharge = paymentContext?.codHandlingCharge;
  const setIsCodMethodSelected = paymentContext?.setIsCodMethodSelected;
  const paymentScreen = paymentContext?.paymentScreen;
  const isCodAvailable = cartInfo?.isCodAvailable;
  const codMessage = cartInfo?.codMessage;

  const deviceType = applicationData?.deviceType;
  const codNudge = paymentInfo?.nudge;

  const subTitle = getStringFromId(
    VERNACULAR_STRING_IDS.CASH_ON_DELIVERY_SUBTITLE,
    VERNACULAR_DEFAULT_MESSAGES.CASH_ON_DELIVERY_SUBTITLE
  );
  const payNowText = getStringFromId(
    VERNACULAR_STRING_IDS.PLACE_ORDER,
    VERNACULAR_DEFAULT_MESSAGES.PLACE_ORDER
  );
  let title = getStringFromId(
    VERNACULAR_STRING_IDS.CASH_ON_DELIVERY_TITLE,
    VERNACULAR_DEFAULT_MESSAGES.CASH_ON_DELIVERY_TITLE
  );

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    title = getStringFromId(
      VERNACULAR_STRING_IDS.CASH_ON_DELIVERY_TITLE_DESKTOP,
      VERNACULAR_DEFAULT_MESSAGES.CASH_ON_DELIVERY_TITLE_DESKTOP
    );
  }


  /**
   * nudgeConfig contains details of nudge like text, htmlText, icon, ctaText, mode and ctaClickable
   */
  const nudgeConfig = getNudgeConfig({
    grandTotal,
    codNudge,
    codHandlingCharge,
    toggleAccordian: (method: string) => toggleNewPaymentAccordian(method, isDisabled),
    trackCODNudgeCtaClick,
    setIsCodMethodSelected,
    newPaymentMethodWithPriority,
    newPaymentMethod,
    deviceType,
    platform,
    paymentScreen,
    disableNudge,
    history,
    theme,
  });

  useEffect(() => {
    if (nudgeConfig?.nudgeText && isAccordianOpened) {
      // cod nudge visibility tracking
      trackCodNudgeVisible();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isAccordianOpened]);

  const handleDoPayment = () => {
    /**
     * if grandTotal is 0 then we are sending codCharges as 0
     */
    const codCharges = grandTotal !== 0 ? codHandlingCharge : 0;

    //call the doPayment function cod payment option is available
    if (isCodAvailable) {
      doPayment(PAYMENT_MODES.COD, {
        codCharges,
      });
    }
  };
  /**
   * configuration for disabled cod message, on the basis of paymentTags
   * cod will be disabled when
   */
  const codDisabledMessageConfig = {
    codMessage,
    payNowDisabled: !isCodAvailable,
    showCodDisabledMessage: !!(!isCodAvailable && codMessage), // check here
    leadingIcon: getIcons(platform, ICONS_NAMES.ALERT),
    codMessageColor: theme?.colors?.warning,
  };

  const config : CodMobileConfig | CodDesktopConfig = {
    title,
    subTitle,
    leadingIcon: getIcons(platform, ICONS_NAMES.COD),
    endIcon,
    toggleAccordian: () => toggleNewPaymentAccordian(PAYMENT_MODES.COD, isDisabled),
    isAccordianOpened,
    payNowText,
    // totalAmount: grandTotal,
    doPayment: handleDoPayment,
    nudgeLeadingIcon: nudgeConfig?.nudgeLeadingIcon,
    nudgeHtmlText: nudgeConfig?.nudgeHtmlText,
    nudgeCtaClickable: nudgeConfig?.nudgeCtaClickable,
    nudgeCtaText: nudgeConfig?.nudgeCtaText,
    onNudgeCtaClick: nudgeConfig?.onNudgeCtaClick,
    nudgeCtaEndIcon: nudgeConfig?.ctaEndIcon,
    showNudgeBackground: nudgeConfig?.showNudgeBackground,
    nudgeCtaHTMLText: nudgeConfig?.nudgeCtaHTMLText,
    isDisabled,
    theme,
    buttonType: BUTTON_TYPE.MEDIUM,
    codDisabledMessageConfig,
    isButtonFixedToBottom
  };

  // config changes for payment Failure Bottomsheet
  if (paymentFailureFlow && deviceType !== DEVICE_TYPE.DESKTOP) {
    config.noBorder = true;
    config.noMargin = true;
    config.showPayButton = false;
    config.subTitle = codHandlingCharge && `Cash handling fee: ₹${codHandlingCharge}`;
    config.isAccordianOpened = false; // isAccordianOpened - true has some space for content also
    config.subTitleFont = theme.typography.bodyMedium;
  }

  if (deviceType === DEVICE_TYPE.DESKTOP) {
    return <CashOnDeliveryDesktop {...config} />;
  }

  return <CashOnDelivery {...config} theme={theme} />;
};

export default CashOnDeliveryWrapper;
